import moment from 'moment'
import { mapState, mapMutations } from 'vuex'

// Importing components
import SearchCustomDropdown from '../common/search/SearchCustomDropdown.vue'
import CustomDropdownGroup from '../common/search/CustomDropdownGroup.vue'
import CheckboxListSelector from '../search/components/CheckboxListSelector.vue'
import KeywordInput from '../search/components/KeywordInput.vue'
import RangeInputDropdown from '../search/components/RangeInputDropdown.vue'

// Importing constants
import { isEmpty, commaSeparatedbyObjectKey } from '../../utils/generalUtils'
import {
  searchOptions,
  areaOptions,
  yearOptions,
  lotSizeOptions,
  subPropertyOptions
} from '../common/searchPropertyConst'
import { generateCustomPriceText } from '../../services/autoSearchService'

const DEFAULT_INPUT_VALUES = {
  cityBtn: { text: 'City', state: false },
  typeBtn: { text: 'All Types', state: false },
  priceBtn: { text: 'Any Price', state: false },
  bedBathBtn: { text: 'All Beds & Baths', state: false },
  includeBtn: { text: 'Include', state: false },
  filterBtn: { text: 'Filters', state: false },
  petBtn: { text: 'Pets Not Allowed', state: false },
  moreBtn: { text: 'More Filters', state: false }
}

const resetTextFields = {
  selectedTypes: [],
  selectedPrice: { minPrice: '0', maxPrice: '10000000' },
  selectedListings: [],
  selectedPetOptions: []
}

export default {
  components: {
    SearchCustomDropdown,
    CustomDropdownGroup,
    CheckboxListSelector,
    KeywordInput,
    RangeInputDropdown
  },

  data() {
    return {
      ...resetTextFields,
      searchInput: {},
      newSearchQuery: { ...searchOptions.INITIAL_NEW_QUERY },
      searchOptions,
      bedBathParams: [
        { name: 'Beds', options: searchOptions.dropdownNoOfBeds },
        { name: 'Baths', options: searchOptions.dropdownNoOfBaths }
      ],
      areaOptions: areaOptions(),
      areaParams: {
        title: 'Square Feet',
        custom: true,
        suffix: ' sq ft',
        alternate: true,
        width: '12rem',
        min: '0',
        max: '20000'
      },
      lotSizeOptions: lotSizeOptions(),
      lotSizeParams: {
        title: 'Lot Size',
        custom: true,
        suffix: ' acres',
        alternate: true,
        width: '12rem',
        min: '0',
        max: '10'
      },
      yearOptions: yearOptions(),
      yearParams: {
        title: 'Year Built',
        alternate: true,
        width: '12rem',
        min: '0',
        max: moment().format('YYYY')
      },
      lotSize: ''
    }
  },

  computed: {
    ...mapState('common', [
      'searchObject',
      'lastNewQuery',
      'isMobile',
      'searchedCities',
      'addressInputValue'
    ]),

    isRentType() {
      return this.searchObject.searchType === 'rent'
    },

    showOnly() {
      return this.searchObject.includeType === 1
    },

    isPendingIncluded() {
      return this.lastNewQuery.query.Status.includes('Pending')
    }
  },

  watch: {
    lastNewQuery: {
      handler: function(value) {
        // console.log('last new query >>>', value)
        this.newSearchQuery = { ...value.query }
        this.setAllInputBoxesParams()
      },
      deep: true
    }
  },

  created() {
    // This is the new updated search object
    this.newSearchQuery = { ...this.lastNewQuery.query }
    this.searchInput =
      this.searchObject.searchType === 'rent'
        ? JSON.parse(JSON.stringify(DEFAULT_INPUT_VALUES))
        : JSON.parse(
          JSON.stringify({
            ...DEFAULT_INPUT_VALUES,
            cityBtn: {
              text: this.addressInputValue.placeholder,
              state: false
            }
          })
        )
  },

  mounted() {
    this.setAllInputBoxesParams()
  },

  methods: {
    ...mapMutations('common', {
      setSearchResultPage: 'SET_SEARCH_RESULT_PAGE',
      setNewRequestedQuery: 'SET_NEW_REQUESTED_QUERY'
    }),

    setAllInputBoxesParams() {
      // Use this once array is supported
      if (!isEmpty(this.lastNewQuery.query.Price)) {
        this.setPriceText(this.lastNewQuery.query.Price)
      } else {
        this.searchInput.priceBtn = { ...DEFAULT_INPUT_VALUES.priceBtn }
        this.selectedPrice = { ...resetTextFields.selectedPrice }
      }
      this.setBedBathText()

      // Set the city box text value if continued search
      if (this.isRentType) {
        this.searchInput.cityBtn.text = this.lastNewQuery.query.City || 'City'
      } else {
        if (this.lastNewQuery.query.MLSNumber) {
          this.searchInput.cityBtn.text = this.lastNewQuery.query.MLSNumber
        } else if (this.lastNewQuery.query.PostalCode) {
          this.searchInput.cityBtn.text = this.lastNewQuery.query.PostalCode
        } else {
          this.searchInput.cityBtn.text =
            this.lastNewQuery.query.City || this.addressInputValue.placeholder
        }
      }

      // Set the property type box text value
      this.setPropertyTypeValues()

      // Set the include type box text value
      // For now this text box will set value for pending only
      // This text box will also include foreclosure text (waiting on API update)
      if (this.isPendingIncluded) {
        if (
          isEmpty(this.lastNewQuery.query.Foreclosure) ||
          this.lastNewQuery.query.Foreclosure === true
        ) {
          this.selectedListings = [...searchOptions.listingOptions]
        } else {
          this.selectedListings = [
            { text: 'Pending Listings', value: 'pending' }
          ]
        }
      } else if (
        isEmpty(this.lastNewQuery.query.Foreclosure) ||
        this.lastNewQuery.query.Foreclosure === true
      ) {
        this.selectedListings = [{ text: 'Foreclosure', value: 'foreclosure' }]
      }

      if (this.searchObject.searchType === 'sale') {
        // set sale pre-values here
      } else if (this.searchObject.searchType === 'rent') {
        // set rent pre-values here
        this.selectedPetOptions = this.lastNewQuery.query.Pets
          ? searchOptions.petOptions
          : []
      }
      this.displayFilterText()
    },

    /**
     * Checks for value entered in city input and updates search query
     * based on entered value
     * @param {String} enteredValue - city search input
     */
    setCitySearchInput(enteredValue) {
      if (isNaN(enteredValue) || this.isRentType) {
        this.setNewRequestedQuery({
          ...this.lastNewQuery.query,
          City: enteredValue,
          MLSNumber: '',
          PostalCode: ''
        })
      } else {
        if (enteredValue.length === 5) {
          this.setNewRequestedQuery({
            ...this.lastNewQuery.query,
            City: '',
            MLSNumber: '',
            PostalCode: enteredValue
          })
        } else {
          this.setNewRequestedQuery({
            ...this.lastNewQuery.query,
            City: '',
            MLSNumber: enteredValue,
            PostalCode: ''
          })
        }
      }
    },

    setPropertyTypeValues() {
      if (isEmpty(this.lastNewQuery.query.PropertySubType)) {
        this.selectedTypes = []
        if (this.isRentType) {
          this.searchInput.typeBtn = { ...DEFAULT_INPUT_VALUES.typeBtn }
        } else {
          this.searchInput.typeBtn.text = `All Types${this.getListingText()}`
        }
      } else {
        // This is temporary setting
        let currentText = this.searchInput.typeBtn.text
        if (currentText.includes(';')) {
          currentText = currentText.slice(0, currentText.indexOf(';'))
        }
        const typeArray = this.lastNewQuery.query.PropertySubType.split(',')

        let updatedOptions = [...subPropertyOptions]
        if (this.isRentType) {
          updatedOptions = [
            ...updatedOptions,
            { text: 'Apartment', value: 'Apartment' }
            // { text: 'Efficiency', value: 'efficiency' }
          ]
        } else {
          updatedOptions = [
            ...updatedOptions,
            { text: 'Multi Family', value: 'MultiFamily' }
          ]
        }

        this.selectedTypes = updatedOptions.filter(option =>
          typeArray.includes(option.text)
        )
        if (!this.isRentType) {
          this.searchInput.typeBtn.text = `${currentText}${this.getListingText()}`
        }
      }
    },

    getListingText() {
      let includeText = ''
      const currentStatus = this.lastNewQuery.query.Status
      const foreclosureValue = this.lastNewQuery.query.Foreclosure
      if (this.searchObject.includeType === 0) {
        if (currentStatus.includes('Pending')) {
          if (isEmpty(foreclosureValue)) {
            includeText = 'Include Pending + Foreclosures'
          } else if (foreclosureValue === false) {
            includeText = 'Include Pending'
          }
        } else {
          if (isEmpty(foreclosureValue)) {
            includeText = 'Include Foreclosures'
          }
        }
      } else {
        if (currentStatus.includes('Pending')) {
          if (foreclosureValue === true) {
            includeText = 'Pending Foreclosures Only'
          } else {
            includeText = 'Pending Only'
          }
        } else {
          if (foreclosureValue === true) {
            includeText = 'Foreclosures Only'
          }
        }
      }
      return isEmpty(includeText) ? '' : `; ${includeText}`
    },

    onBedBathSelected({ type, option }) {
      this.setNewRequestedQuery({
        ...this.lastNewQuery.query,
        [type]: option.value
      })
      this.setBedBathText()
    },

    setBedBathText() {
      const beds = this.lastNewQuery.query.Beds
        ? `${this.lastNewQuery.query.Beds}+`
        : 'Any'
      const baths = this.lastNewQuery.query.Baths
        ? `${this.lastNewQuery.query.Baths}+`
        : 'Any'
      this.searchInput.bedBathBtn.text =
        beds === 'Any' && baths === 'Any'
          ? 'All Beds & Baths'
          : `${beds} Beds x ${baths} Baths`
    },

    updatePriceRange(range, max) {
      this.setNewRequestedQuery({ ...this.lastNewQuery.query, Price: range })
      this.setPriceText(range, max)
    },

    setPriceText(range, max) {
      const { minPrice, maxPrice, customPriceText } = generateCustomPriceText(
        range,
        max,
        this.searchObject.searchType
      )
      this.searchInput.priceBtn.text = customPriceText
      this.selectedPrice = { minPrice, maxPrice }
    },

    updateCheckboxSelections(selectedList, type, text) {
      if (type === 'typeBtn') {
        this.setNewRequestedQuery({
          ...this.lastNewQuery.query,
          PropertySubType: !isEmpty(selectedList)
            ? commaSeparatedbyObjectKey(selectedList, 'text')
            : ''
        })
      } else if (type === 'includeBtn') {
        this.newSearchQuery.Listings = [...selectedList]
        const currentStatus = this.lastNewQuery.query.Status

        if (selectedList.some(item => item.value === 'pending')) {
          if (this.showOnly) {
            // do something different
            this.setNewRequestedQuery({
              ...this.lastNewQuery.query,
              Status: 'Pending'
            })
          } else {
            // this is default behavior
            if (currentStatus && !currentStatus.includes('Pending')) {
              let status =
                currentStatus.slice(-1) === ','
                  ? `${currentStatus}Pending`
                  : `${currentStatus},Pending`
              this.setStatusValue(status)
            }
          }
        } else {
          if (this.showOnly) {
            // do something different
            this.setNewRequestedQuery({
              ...this.lastNewQuery.query,
              Status: 'Active'
            })
          } else {
            // this is default behavior
            if (currentStatus.includes('Pending')) {
              let status =
                currentStatus.localeCompare('Pending') === 0
                  ? ''
                  : currentStatus.replace('Pending', '')
              this.setStatusValue(status)
            }
          }
        }

        if (selectedList.some(item => item.value === 'foreclosure')) {
          if (this.showOnly) {
            this.setNewRequestedQuery({
              ...this.lastNewQuery.query,
              Foreclosure: true
            })
          } else {
            this.setNewRequestedQuery({
              ...this.lastNewQuery.query,
              Foreclosure: ''
            })
          }
        } else {
          this.setNewRequestedQuery({
            ...this.lastNewQuery.query,
            Foreclosure: false
          })
        }
      } else if (type === 'petBtn') {
        this.setNewRequestedQuery({
          ...this.lastNewQuery.query,
          Pets: selectedList.length > 0
        })
      }

      const preTypeText = this.searchInput.typeBtn.text
      if (selectedList.length === 1) {
        this.searchInput[type].text = selectedList[0].text
      } else if (selectedList.length > 1) {
        this.searchInput[type].text = `${
          selectedList[0].text
        } (+${selectedList.length - 1} others)`
      } else {
        this.searchInput[type].text = text
      }
      if (!this.isRentType) {
        let currentText = this.searchInput.typeBtn.text
        if (type === 'includeBtn') {
          let indexOfSemi = currentText.indexOf(';')
          if (indexOfSemi > 0) {
            currentText = currentText.slice(0, indexOfSemi)
          }
          if (selectedList.length < 1) {
            this.searchInput.typeBtn.text = `${currentText}`
          } else {
            this.searchInput.typeBtn.text = `${currentText}${this.getListingText()}`
          }
        }

        if (type === 'typeBtn') {
          if (preTypeText.includes(';')) {
            this.searchInput.typeBtn.text = `${currentText}${preTypeText.slice(
              preTypeText.indexOf(';')
            )}`
          }
        }
      }
    },

    setStatusValue(status) {
      if (status.charAt(0) === ',') status = status.slice(0, 1)
      if (status.charAt(status.length - 1) === ',') {
        status = status.slice(0, -1)
      }
      this.setNewRequestedQuery({
        ...this.lastNewQuery.query,
        Status: status
      })
    },

    updateKeyword(keywords) {
      this.setNewRequestedQuery({
        ...this.lastNewQuery.query,
        Keywords: keywords
      })
      this.displayFilterText()
    },

    updateRangeValue(type, value) {
      this.setNewRequestedQuery({ ...this.lastNewQuery.query, [type]: value })
      this.displayFilterText()
    },

    displayFilterText() {
      let totalFilters = 0
      if (!isEmpty(this.lastNewQuery.query.Keywords)) totalFilters++
      if (
        this.lastNewQuery.query.SqFt &&
        this.lastNewQuery.query.SqFt !== `0-20000`
      ) {
        totalFilters++
      }
      if (this.searchObject.searchType === 'rent') {
        // Check rent specific values here
      } else {
        // Check sale specific values here
        if (
          this.lastNewQuery.query.LotSizeArea &&
          this.lastNewQuery.query.LotSizeArea !== `0-10`
        ) {
          totalFilters++
        }
        if (
          this.lastNewQuery.query.YearBuilt &&
          this.lastNewQuery.query.YearBuilt !== `0-${moment().format('YYYY')}`
        ) {
          totalFilters++
        }
      }
      this.searchInput.filterBtn.text =
        totalFilters > 0 ? `${totalFilters} Filters` : 'Filters'
      return totalFilters > 0
    },

    /**
     * Handles the click event of Search button
     * Sends request to the server to retrieve data based
     * on the options entered
     */
    searchProperties() {
      // *********************************************************************
      // This part is created for handling the results search component values
      // *********************************************************************
      setTimeout(() => {
        let requestedQuery = {
          ...this.newSearchQuery,
          PropertyType:
            this.searchObject.searchType === 'rent' ? 'ResidentialLease' : 'Residential',
          StateOrProvince: ''
        }

        if (this.searchObject.searchType === 'sold') {
          requestedQuery = { ...requestedQuery, Status: 'Sold' }
        }

        // *********************************************************************
        // Temporary code part (not yet supported by backend)
        delete requestedQuery.Amenities
        delete requestedQuery.Housing
        delete requestedQuery.Listings
        if (!this.isRentType) {
          delete requestedQuery.Pets
        } else {
          delete requestedQuery.Foreclosure
        }
        // *********************************************************************

        this.setSearchResultPage(true)
        this.setNewRequestedQuery(requestedQuery)

        // *********************************************************************
        this.$emit('on-search-clicked')
      }, 100)
    }
  }
}
